import { callRequest } from 'services/callRequestManager';
import configs from 'configs';
import { saveItem, loadItem, saveSessionItem, loadSessionItem } from 'utils';
import getLogger from 'utils/getLogger';

const logger = getLogger('baoLoginManager');

export type CurrentEnv = {
  ENV: string;
};

export const getEnvBaseUrl = (): string => {
  const appContextPath = window.location.pathname.split('/')[1];
  return `${window.location.origin}/${appContextPath}`;
};

const CSRF_COOKIE_NAME = 'XSRF-TOKEN';

const hasCsrfCookie = (): boolean => {
  return document.cookie.split(';').some((c) => c.trim().startsWith(`${CSRF_COOKIE_NAME}=`));
};

const fetchConfigEnv = (): Promise<CurrentEnv> => {
  return callRequest<CurrentEnv>({
    url: `${configs.params.LOGIN_MANAGER_SERVER.BASE_URL}${configs.params.LOGIN_MANAGER_SERVER.ENDPOINTS.CONFIG_ENV}`,
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    resType: 'json',
  });
};

export const getCurrentEnv = (): Promise<string> => {
  const DEV_ENV = loadItem('DEV_ENV');
  if (DEV_ENV) {
    if (!hasCsrfCookie()) {
      // Fire GET to establish the CSRF cookie even when env is resolved locally
      const p = fetchConfigEnv();
      if (p && p.catch) {
        p.catch(() => {});
      }
    }
    return Promise.resolve(DEV_ENV);
  }

  // Detect PP from hostname to avoid chicken-and-egg: the /config/env API URL
  // depends on configs.params which in turn depends on knowing the env first.
  // When behind the reverse proxy (accounts.pprod.nbdb.ca), hostname contains 'pprod.'
  const host = window.location.host || '';
  if (host.includes('pprod.') || host.includes('iiroc-pp.investments')) {
    saveItem('CURRENT_ENV', 'pp');
    return Promise.resolve('pp');
  }

  // Check if CURRENT_ENV is already cached in sessionStorage for the current env base URL
  const cachedEnv = loadSessionItem('CURRENT_ENV');
  const cachedUrl = loadSessionItem('CURRENT_ENV_URL');
  if (cachedEnv && cachedUrl === getEnvBaseUrl()) {
    if (!hasCsrfCookie()) {
      // The CSRF cookie was lost (session expired, browser restart) — re-fetch to obtain it
      return fetchConfigEnv()
        .then(() => cachedEnv)
        .catch(() => cachedEnv);
    }
    return Promise.resolve(cachedEnv);
  }

  return fetchConfigEnv()
    .catch((err) => {
      // On the local environments ONLY, when 'DEV_ENV' is not set, this call will not work because it point to localhost.
      // Default to development configurations in that case.
      // If this is not a development build, default to common configurations instead.
      // TODO : Redirect to an error page instead, once we have such a page.
      if (process.env.NODE_ENV === 'development') {
        const env: CurrentEnv = {
          ENV: 'local',
        };
        return env;
      }

      logger.error(`Error getting current environment`, err);
      throw err;
    })
    .then((json: CurrentEnv) => {
      const currentEnv = json.ENV;

      // Save to localStorage for the usage of oktaService.getCurrentEnv()
      saveItem('CURRENT_ENV', currentEnv);
      // Save to sessionStorage to cache the env and avoid repeated API calls on refresh.
      saveSessionItem('CURRENT_ENV', currentEnv);
      // Save to sessionStorage to invalidate the cache if the user switches env within same tab (e.g. /bao-tu → /bao-ti).
      saveSessionItem('CURRENT_ENV_URL', getEnvBaseUrl());
      return currentEnv;
    });
};
